import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { getReducedFiendlyName, getRoundName } from '../common/RoundHelper.js';
import { PoolXNumberFormat } from '../common/CurrencyFormatter.js';
import { Button, TicketIcon } from '../reusable-components.js';
import { UnibetReceiptIcon } from '../images/unibeticons';
import { Round } from '../models/round.js';
import { CouponShare, CouponShareCouponCoupon } from '../models/couponshares.js';
import { Grid, SxProps, Theme, Typography } from '@mui/material';
import ShareDialog from './sharedialog';
import { getCurrentToken } from '../backend/fetchApi.js';
import { themeC } from '../theme/themehelper';


interface CouponInfoProps {
    round?: Round;
    couponShare?: CouponShare;
    isRightToLeft?: boolean;
    shareEnabled?: boolean;
}

const CouponInfo: React.FC<CouponInfoProps> = ({ round, couponShare, isRightToLeft, shareEnabled }) => {
    const { t } = useTranslation();
    let [showShareDialog, setShowShareDialog] = useState(false);
    let [publicUrl, setPublicUrl] = useState<string | undefined>(undefined);

    useEffect(() => {
        setPublicUrl(couponShare?.publicUrl);
    }, [couponShare]
    )

    if (!couponShare || !round) {
        return <></>;
    }

    const { stake, coupon, createdAt, agentFee, fee } = couponShare;
    const { operatorStakePerRow, openTo } = round;
    const { totalRows = 0 } = coupon || {};

    let totAmount = 0;

    if (stake?.amount != null) totAmount += Number(stake.amount);
    if (fee?.amount != null) totAmount += Number(fee.amount);
    if (agentFee?.amount != null) totAmount += Number(agentFee.amount);

    const roundName = getRoundName(round, t);

    //
    // -------- TEXT FILE DOWNLOAD --------
    //
    const downloadTxtFile = (couponId: string, roundId: string, couponObj: CouponShareCouponCoupon | undefined) => {
        if (!couponObj?.bets) return;

        let outString = "";

        outString += roundId + '\r\n';

        couponObj.bets.forEach(bet => {
            bet.rows?.forEach(row => {
                outString += 'E,';
                row.signs.forEach(sign => {
                    let text = sign === "TWO" ? "2" : sign === "X" ? "X" : "1";
                    outString += text + ',';
                });
                outString = outString.slice(0, -1);
                outString += '\r\n';
            });
        });

        const element = document.createElement("a");
        const file = new Blob([outString], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `poolx_rows_${couponId}.txt`;
        document.body.appendChild(element);
        element.click();
        element.remove();
    };

    const shareCoupon = async (couponShare: CouponShare) => {
        const headers = new Headers({
            Accept: 'application/json',
        });

        const token = getCurrentToken();
        if (token) {
            headers.set('Authorization', "Bearer " + token);
        }


        const res = await fetch(new URL(couponShare._links.publish.href), {
            headers,
            method: 'POST'
        });

        if (res.ok) {
            var data = await res.json();
            setPublicUrl((data as CouponShare).publicUrl);
            setShowShareDialog(true);
        }
    }

    const intlOptions: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
    };

    const datef = new Intl.DateTimeFormat('sv-SV', intlOptions);

    const couponBets = coupon?.coupon?.bets;
    let betType = t('single_rows');
    let reducedSystemName = "";

    if (couponBets && couponBets.length === 1 && couponBets[0].system) {
        const name = couponBets[0].system.type;

        if (name === "MATHEMATICAL") betType = t('mathematical_system');
        if (name === "REDUCED") betType = t('reduced_system');

        reducedSystemName = getReducedFiendlyName(couponBets[0].system.reducedSystemId);
    }

    if (!couponBets) {
        betType = t('syndicate_not_finalised');
    }

    const share = Math.round((couponShare.stakeUnits / couponShare.totalStakeUnits) * 100);

    const locale = t('locale');
    const nf = PoolXNumberFormat(locale, {
        style: 'currency',
        currency: operatorStakePerRow.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const isAfterDeadline = new Date(openTo) < new Date();

    const started = isAfterDeadline ? t('started') : t('starts');

    const styleGrid: SxProps<Theme> = {
        background: "var(--background_3)",
        padding: "16px 16px 16px 16px",
        color: "var(--text-black-dark-background)",
    };

    const styleHeader: SxProps<Theme> = {
        margin: "9px 0 14px 0",
        fontSize: "11px",
        fontWeight: "bold"
    };

    const styleText: SxProps<Theme> = {
        fontSize: "11px",
        fontWeight: "normal"
    };

    const styleRow: SxProps<Theme> = {
        width: "calc(100% - 33px)",
        padding: "8px 0px 8px 0px",
        borderBottom: "1px solid var(--text-black-dark-background)",
    };

    return (

        <Grid container direction="column" alignItems="center" sx={styleGrid}>

            {window.theme === themeC.Green ? <UnibetReceiptIcon width={20} height={20} fill="#111111" /> : TicketIcon}

            <Typography sx={styleHeader}>
                {isRightToLeft ? `${t('rows')} ${totalRows}` : `${totalRows} ${t('rows')}`}
            </Typography>

            <Grid container justifyContent="space-between" sx={styleRow}>
                <Typography sx={styleText}>
                    {isRightToLeft
                        ? `${nf.format(totAmount)} :${t('total_cost')}`
                        : `${t('total_cost')}: ${nf.format(totAmount)}`}
                </Typography>
                <Typography sx={styleText}>
                    {isRightToLeft
                        ? `${datef.format(new Date(createdAt!))} :${t('paid')}`
                        : `${t('paid')}: ${datef.format(new Date(createdAt!))}`}
                </Typography>
            </Grid>

            <Grid container justifyContent="space-between" sx={styleRow}>
                <Typography sx={styleText}>
                    {isRightToLeft ? `${roundName} :${t('round')}` : `${t('round')}: ${roundName}`}
                </Typography>
                <Typography sx={styleText}>
                    {isRightToLeft
                        ? `${datef.format(new Date(openTo))} :${started}`
                        : `${started}: ${datef.format(new Date(openTo))}`}
                </Typography>
            </Grid>

            {share < 100 && (
                <Grid container justifyContent="space-between" sx={styleRow}>
                    <Typography sx={styleText}>
                        {t('share_of_syndicate')}: {share}%
                    </Typography>
                </Grid>
            )}

            <Grid sx={styleRow}>
                <Typography sx={styleText}>{betType}</Typography>
                <Typography sx={styleText}>{reducedSystemName}</Typography>
            </Grid>

            <Grid sx={styleRow}>
                <Typography sx={styleText}>
                    {isRightToLeft ? `${couponShare.id} :${t('id')}` : `${t('id')}: ${couponShare.id}`}
                </Typography>
            </Grid>

            <Grid container direction="row" justifyContent="center" sx={{ marginTop: "20px" }}>
                {!(window as any).nativeApp &&
                <Button padding="12px 20px" margin="10px" onClick={() => downloadTxtFile(coupon?.id ?? "", coupon?.roundId ?? "", coupon?.coupon)}>
                    {t('print')}
                </Button>}
                {shareEnabled &&
                    <Button padding="12px 20px" margin="10px" onClick={() => shareCoupon(couponShare)}>
                        {t('share')}
                    </Button>}
            </Grid>
            {showShareDialog && publicUrl && (
                <ShareDialog publicUrl={publicUrl} open={showShareDialog} onClose={() => setShowShareDialog(false)} />
            )}
        </Grid>

    );
};

export default CouponInfo;
